<template>
	<div class="pronto-radio-group" :class="{ small, large }">
		<div
			class="pronto-radio-item"
			v-for="opt in options"
			:key="opt.value"
			:class="{ selected: value === opt.value, disabled: opt.disabled }"
			tabindex="0"
			@click="onSelect(opt)"
			@keyup.enter="onSelect(opt)"
			@keyup.space="onSelect(opt)"
		>
			<slot name="item" :data="opt">
				<div class="pronto-radio-item-left">
					<slot :data="opt">
						<div class="pronto-radio-icon">
							<slot name="icon" :data="opt">
								<component
									v-if="opt.icon"
									:is="opt.icon"
									v-bind="opt.iconProps || {}"
								/>
							</slot>
						</div>
						<div class="pronto-radio-text">
							<div
								class="pronto-radio-label"
								v-text="localize ? $t(opt.label) : opt.label"
							/>
							<div
								class="pronto-radio-description"
								v-if="opt.desc"
								v-text="localize ? $t(opt.desc) : opt.desc"
							/>
						</div>
					</slot>
				</div>
				<div class="pronto-radio-item-right">
					<slot name="right" :data="opt">
						<div class="pronto-radio-selector">
							<slot name="selector" :data="opt">
								<CheckIcon v-if="value === opt.value" type="regular" />
								<CircleIcon v-else />
							</slot>
						</div>
					</slot>
				</div>
			</slot>
		</div>
	</div>
</template>

<script>
import { CircleIcon, CheckIcon } from '@/icons'

const sizes = {
	SMALL: 'small', // default size
	LARGE: 'large', // not yet implemented
}

export default {
	name: 'RadioGroup',
	components: { CircleIcon, CheckIcon },
	props: {
		modelValue: null,
		options: Array,
		localize: { type: Boolean, default: null },
		// size props are just boolean flags
		...Object.values(sizes).reduce(
			(acc, s) => ({ ...acc, [s]: { type: Boolean, default: null } }),
			{}
		),
	},
	data() {
		return { value: this.modelValue }
	},
	watch: {
		modelValue(modelValue) {
			this.value = modelValue
		},
		value(value) {
			this.$emit('update:modelValue', value)
			this.$emit('change', value) // additional event to handle v2/v3 backwards compatibility
		},
	},
	emits: ['update:modelValue', 'change'],
	methods: {
		onSelect({ value, disabled }) {
			if (disabled) return false
			this.value = value
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

$height-sm: 56px;
$icon-size-sm: 17px;
$font-size-sm: 15px;

.pronto-radio-group {
	background: var(--neutral-0);
	border: 1px solid var(--neutral-200);
	border-radius: 6px;
	overflow: hidden;
	.pronto-radio-item {
		@include flex-spread;
		cursor: pointer;
		padding: 0 16px;
		height: $height-sm;
		color: var(--neutral-800);
		font-size: $font-size-sm;
		border-bottom: 1px solid var(--neutral-200);
		&.selected {
			.pronto-radio-item-right {
				.pronto-radio-selector {
					color: var(--blue-300);
				}
			}
		}
		&.disabled {
			cursor: default;
			.pronto-radio-selector {
				display: none !important;
			}
		}
		&:last-child {
			border-bottom: 0;
		}
		.pronto-radio-item-left {
			@include flex;
			flex-grow: 1;
			max-width: calc(100% - 30px);
			.pronto-radio-icon {
				@include flex;
				flex-shrink: 0;
				font-size: 22px;
				color: var(--neutral-600);
				margin-right: 8px;
			}
			.pronto-radio-text {
				flex-grow: 1;
				max-width: 100%;
				@include flex;
				flex-wrap: wrap;
				column-gap: 8px;
				.pronto-radio-label {
					@include ellipsis;
				}
				.pronto-radio-description {
					font-size: 14px;
					color: var(--neutral-400);
					@include ellipsis;
				}
			}
		}
		.pronto-radio-item-right {
			@include flex;
			justify-content: flex-end;
			margin-left: 8px;
			.pronto-radio-selector {
				@include flex;
				justify-content: flex-end;
				color: var(--neutral-400);
				.pronto-icon {
					font-size: $icon-size-sm;
				}
			}
		}
	}
}
</style>
